import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
// CORE-1802 — ленивый дашборд: жадный импорт отсюда (App.tsx держит маршрут
// жадно) тащил весь дашборд, юр. тексты и профиль мастера в главный чанк.
// Экран рендерится внутри Suspense навигатора App.tsx.
import { DashboardScreen } from './lazy';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import type { RootStackParamList } from '../navigation/types';

import { accentTextOn, ON_ACCENT_TEXT } from '../styles/accentText';
type Nav = NativeStackNavigationProp<RootStackParamList, 'GamificationShare'>;
type Route = RouteProp<RootStackParamList, 'GamificationShare'>;

type State =
  | { status: 'loading' }
  | { status: 'ready'; userId: string }
  | { status: 'unavailable' };

/**
 * `/s/:token` — where a shared «Путь Света» card lands a human.
 *
 * CORE-966 — the same URL is what Telegram/WhatsApp/Facebook fetch for their
 * preview (a Vercel Function answers them with the card's Open Graph tags); a
 * person following the link gets this screen. The token says nothing on its own,
 * so whose Light Path to open is read from the public share endpoint — the very
 * same anonymous read the crawler performs, which is why a guest sees exactly
 * what the preview promised.
 *
 * The target screen is RENDERED, not dispatched to. CORE-675 and CORE-528 both
 * ended here: a `navigation.replace` from a mount effect is silently discarded
 * on a cold load — and a share link is, by definition, always a cold load. That
 * also keeps `/s/:token` in the address bar, so the address a person copies from
 * the URL bar is the same stable link they were sent.
 */
export const GamificationShareLinkRoute: React.FC = () => {
    const navigation = useNavigation<Nav>();
    const route = useRoute<Route>();
    const token = (route.params as { token?: string } | undefined)?.token;
    const { user } = useAuth();
    const { isDark } = useTheme();
    const [state, setState] = useState<State>({ status: 'loading' });

    useEffect(() => {
        if (!token) {
            setState({ status: 'unavailable' });
            return;
        }
        let alive = true;
        setState({ status: 'loading' });
        api.getPublicGamificationShareCard(token)
            .then((card) => {
                if (!alive) return;
                setState(card?.userId
                    ? { status: 'ready', userId: card.userId }
                    : { status: 'unavailable' });
            })
            // Every failure is the same «this link no longer opens anything»:
            // a decayed streak, a profile turned private and a mistyped token
            // are not different answers to give a visitor.
            .catch(() => { if (alive) setState({ status: 'unavailable' }); });
        return () => { alive = false; };
    }, [token]);

    const isOwner = state.status === 'ready' && !!user?.id && user.id === state.userId;

    const dashboardRoute = useMemo(() => ({
        params: {
            // Own link opened by its owner: their own «Путь Света», not the
            // read-only public view of themselves.
            viewedUserId: state.status === 'ready' && !isOwner ? state.userId : undefined,
            initialTab: 'lightPath' as const,
        },
    }), [state, isOwner]);

    if (state.status === 'ready') {
        return <DashboardScreen navigation={navigation as never} route={dashboardRoute} />;
    }

    const ink = isDark ? '#F8EEE5' : '#2C251F';
    const muted = isDark ? 'rgba(239,228,218,0.72)' : 'rgba(64,54,46,0.72)';

    return (
        // CORE-1733 — ожидание и «ссылка недоступна» на общем фоне v4 (на вебе).
        <View style={[styles.container, { backgroundColor: Platform.OS === 'web' ? 'transparent' : (isDark ? '#141110' : '#FFFCF6') }]}>
            {state.status === 'loading' ? (
                <ActivityIndicator color={accentTextOn('#E36F1D')} />
            ) : (
                <>
                    <Text style={[styles.title, { color: ink }]}>Эта ссылка больше не открывается</Text>
                    <Text style={[styles.body, { color: muted }]}>
                        Карточка могла стать закрытой или устареть. Загляни на главную — там много живого.
                    </Text>
                    <Pressable
                        accessibilityRole="button"
                        onPress={() => navigation.reset({ index: 0, routes: [{ name: 'Home' }] })}
                        style={styles.button}
                    >
                        <Text style={styles.buttonText}>На главную</Text>
                    </Pressable>
                </>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 12 },
    title: { fontSize: 20, fontWeight: '700', textAlign: 'center' },
    body: { fontSize: 15, lineHeight: 22, textAlign: 'center', maxWidth: 420 },
    button: {
        marginTop: 8,
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 999,
        // CORE-1733 (возврат QA) — фирменный #E98245: тёмная подпись 5.1:1 (на #E36F1D — 4.4).
        backgroundColor: '#E98245',
    },
    buttonText: { color: ON_ACCENT_TEXT, fontSize: 15, fontWeight: '700' },
});

export default GamificationShareLinkRoute;
